/**
 * LandingPageNew — Phase 3 conversion-optimized landing behind ENABLE_NEW_LANDING_PAGE
 *
 * Design: Deep navy + slate clinical aesthetic — NOT generic purple/white AI slop.
 * Tier comparison is the centrepiece. CTAs respect ENABLE_PREMIUM_TRIALS flag.
 * PHI-Zero: no user text is collected or logged here.
 * Accessibility: keyboard nav, aria-labels, visible focus states.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Button } from '../components/ui/button';
import {
  BookOpen, Mail, Library, Users, Smartphone, Headphones,
  CheckCircle2, Lock, ChevronDown, ChevronUp, ShieldCheck,
  ArrowRight, Zap, Download
} from 'lucide-react';

// ── Tier data ──────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    subtitle: 'Reader',
    price: 'Free forever',
    highlight: false,
    badge: null,
    features: [
      { text: 'Personalized specialty digests (email + in-app)', on: true },
      { text: 'Up to 3 digests/day, 10 articles each', on: true },
      { text: 'Literature search & exploration', on: true },
      { text: 'Personal library (100 articles)', on: true },
      { text: 'Reading progress & notes (PHI-safe)', on: true },
      { text: '1 Run Now request / 24 h', on: true },
      { text: 'Community reading (view threads)', on: true },
      { text: 'Community posting & commenting', on: false },
      { text: 'Audio takeaways & playlists', on: false },
      { text: 'Library export (CSV & RIS)', on: false },
    ],
    cta: { label: 'Sign up free', href: '/signup', variant: 'outline' },
  },
  {
    id: 'validated',
    name: 'Validated',
    subtitle: 'Verified Peer',
    price: 'Free — verify work email',
    highlight: false,
    badge: 'Most trusted',
    features: [
      { text: 'Everything in Free', on: true },
      { text: 'Community posting & commenting', on: true },
      { text: 'Community reactions & article attachment', on: true },
      { text: 'Verified clinician badge', on: true },
      { text: 'One verification per account (work email)', on: true },
    ],
    cta: { label: 'Get validated', href: null, action: 'validation', variant: 'secondary' },
  },
  {
    id: 'pro',
    name: 'Pro',
    subtitle: 'Premium',
    price: '$4.99 / month',
    highlight: true,
    badge: 'Most powerful',
    features: [
      { text: 'Everything in Validated', on: true },
      { text: '10 digests/day, 25 articles each', on: true },
      { text: '5 Run Now requests / 24 h', on: true },
      { text: 'Personal library (1,000 articles)', on: true },
      { text: 'Audio takeaways & playlists', on: true },
      { text: 'Library export (CSV & RIS)', on: true },
      { text: 'Lit Scholar AI Assistant (available as enabled during rollout)', on: true },
    ],
    cta: { label: 'View Pro plan', href: '/plan', variant: 'default' },
  },
];

// ── FAQ data ───────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: 'What is PHI-Zero?',
    a: "LitCurrent enforces a strict PHI-Zero policy: the platform is designed for literature discussion, not clinical case discussion. Any message containing patient identifiers (SSNs, dates of birth, medical record numbers, addresses, or phone numbers) is automatically blocked before posting. We log only anonymised identifiers and reason codes — never the raw text.",
  },
  {
    q: 'How does work-email validation work?',
    a: "Enter your institutional work email (e.g., @hospitalname.org) and we send a verification code to that address. Once confirmed, your account is marked as a Validated Peer, unlocking community posting. Verification is free and takes under two minutes. One verification per account.",
  },
  {
    q: 'Which specialties are supported?',
    a: "LitCurrent supports a broad range of specialties including Cardiology, Oncology, Neurology, Endocrinology, Pulmonology, Infectious Disease, Rheumatology, and more. Specialty configurations are regularly updated. You can also add custom topics beyond your primary specialty.",
  },
  {
    q: 'Is there a mobile app?',
    a: "Yes — a mobile companion app (beta) is available for iOS and Android via Expo. It provides digest reading, library management, community browsing, and audio playback. Sign up on the web first to create your account.",
  },
];

// ── FAQ Accordion item ──────────────────────────────────────────────────────

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-700 last:border-0">
      <button
        className="w-full flex items-start justify-between py-5 text-left gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 rounded-sm"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-slate-100">{q}</span>
        {open
          ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />
          : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />}
      </button>
      {open && (
        <p className="pb-5 text-sm text-slate-300 leading-relaxed pr-8">{a}</p>
      )}
    </div>
  );
};

// ── Feature icon card ───────────────────────────────────────────────────────

const FeatureCard = ({ Icon, color, title, description }) => (
  <div className="flex flex-col gap-3 p-6 rounded-xl border border-slate-100 bg-white hover:shadow-md transition-shadow">
    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center shrink-0`}>
      <Icon className="h-5 w-5 text-white" aria-hidden="true" />
    </div>
    <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
    <p className="text-slate-500 text-xs leading-relaxed">{description}</p>
  </div>
);

// ── Tier card ──────────────────────────────────────────────────────────────

const TierCard = ({ tier, trialsEnabled, isAuthenticated, navigate }) => {
  const handleCta = () => {
    if (tier.cta.action === 'validation') {
      navigate(isAuthenticated ? '/verification' : '/signup');
      return;
    }
    if (tier.cta.href) navigate(tier.cta.href);
  };

  // Pro tier: override CTA when trials are enabled
  const proLabel = trialsEnabled && tier.id === 'pro'
    ? 'Start 30-day Pro trial'
    : tier.cta.label;
  const label = tier.id === 'pro' ? proLabel : tier.cta.label;

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-6 sm:p-8 border-2 transition-all
        ${tier.highlight
          ? 'border-amber-400 bg-white shadow-xl shadow-amber-100/50 scale-[1.02]'
          : 'border-slate-200 bg-white shadow-sm hover:shadow-md'}`}
      data-testid={`tier-card-${tier.id}`}
    >
      {tier.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap
          ${tier.highlight ? 'bg-amber-400 text-amber-900' : 'bg-slate-600 text-white'}`}>
          {tier.badge}
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-baseline gap-1.5 mb-1">
          <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
          <span className="text-sm text-slate-500">({tier.subtitle})</span>
        </div>
        <p className={`text-sm font-medium ${tier.highlight ? 'text-amber-600' : 'text-slate-600'}`}>
          {tier.price}
        </p>
      </div>

      <ul className="space-y-2.5 mb-8 flex-1" aria-label={`${tier.name} plan features`}>
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5">
            {f.on
              ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
              : <Lock className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" aria-hidden="true" />}
            <span className={`text-xs leading-snug ${f.on ? 'text-slate-700' : 'text-slate-400'}`}>{f.text}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={handleCta}
        className={`w-full rounded-lg py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2
          ${tier.highlight
            ? 'bg-amber-500 text-white hover:bg-amber-600'
            : tier.cta.variant === 'secondary'
              ? 'bg-slate-100 text-slate-800 hover:bg-slate-200'
              : 'bg-slate-900 text-white hover:bg-slate-700'}`}
        aria-label={`${label} — ${tier.name} plan`}
        data-testid={`tier-cta-${tier.id}`}
      >
        {label}
      </button>

      {trialsEnabled && tier.id === 'pro' && (
        <p className="mt-2 text-center text-[11px] text-slate-400">No credit card required for trial</p>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const LandingPageNew = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const trialsEnabled = useFeatureFlag('enable_premium_trials');

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-new">

      {/* ── Navigation bar ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <span className="text-white font-bold text-lg tracking-tight">LitCurrent</span>
          <nav className="flex items-center gap-2" aria-label="Main navigation">
            {isAuthenticated ? (
              <Button size="sm" onClick={() => navigate('/home')} className="bg-sky-500 hover:bg-sky-600 text-white" aria-label="Go to dashboard">
                Dashboard <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" asChild>
                  <Link to="/login" data-testid="nav-login-link">Log in</Link>
                </Button>
                <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" asChild>
                  <Link to="/signup" data-testid="nav-signup-link">Sign up free</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section
        className="bg-slate-900 pt-20 pb-28 px-4 sm:px-6 text-center"
        aria-labelledby="hero-heading"
      >
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 rounded-full px-4 py-1.5 mb-8">
            <ShieldCheck className="h-3.5 w-3.5 text-sky-400" aria-hidden="true" />
            <span className="text-xs text-sky-300 font-medium">PHI-Zero enforced — no patient identifiers permitted</span>
          </div>

          <h1
            id="hero-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
          >
            Stay current in your specialty,{' '}
            <span className="text-sky-400">without the noise.</span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            LitCurrent curates, summarises, and delivers the latest peer-reviewed literature
            tailored to your specialty — delivered to your inbox and available on any device.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-3 text-base font-semibold rounded-xl shadow-lg shadow-sky-900/30 w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-sky-300"
              asChild
              data-testid="hero-signup-btn"
            >
              <Link to="/signup" aria-label="Create a free account">Sign up free</Link>
            </Button>

            {trialsEnabled ? (
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 text-base font-semibold rounded-xl w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-amber-300"
                onClick={() => navigate('/plan')}
                data-testid="hero-trial-btn"
                aria-label="Start a 30-day Pro trial, no credit card required"
              >
                Start 30-day Pro trial
              </Button>
            ) : (
              <Button
                size="lg"
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white px-8 py-3 text-base rounded-xl w-full sm:w-auto"
                onClick={() => navigate(isAuthenticated ? '/plan' : '/login')}
                data-testid="hero-plans-btn"
                aria-label="View Pro plan"
              >
                View plans
              </Button>
            )}

            <Button
              size="lg"
              variant="ghost"
              className="text-slate-400 hover:text-white px-6 py-3 rounded-xl w-full sm:w-auto"
              asChild
              data-testid="hero-login-btn"
            >
              <Link to="/login" aria-label="Log in to your account">Log in</Link>
            </Button>
          </div>

          {trialsEnabled && (
            <p className="mt-4 text-xs text-slate-500">
              30-day Pro trial available · No credit card required · Cancel-free
            </p>
          )}
        </div>
      </section>

      {/* ── Features grid ───────────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6 bg-slate-50" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 id="features-heading" className="text-3xl font-bold text-slate-900 mb-3">
              Built for clinicians, not consumers
            </h2>
            <p className="text-slate-500 text-base max-w-xl mx-auto">
              Every feature is designed around clinical workflows, specialty-level filtering, and PHI compliance.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              Icon={Mail}
              color="bg-sky-500"
              title="Specialty digests"
              description="Curated from PubMed daily. Configure your specialty, sub-specialty, topics, and favourite journals. Delivered by email and available in-app."
            />
            <FeatureCard
              Icon={BookOpen}
              color="bg-emerald-500"
              title="Literature exploration"
              description="Free-text topic search across recent literature with AI summaries and design-tag filtering (RCT, systematic review, observational)."
            />
            <FeatureCard
              Icon={Library}
              color="bg-violet-500"
              title="Personal library"
              description="Save articles, add PHI-safe notes, track reading progress, and organise into custom topic folders. Export to CSV or RIS (Pro)."
            />
            <FeatureCard
              Icon={Users}
              color="bg-amber-500"
              title="Specialty community"
              description="Topic-threaded discussions among verified clinician peers. PHI-Zero enforced: patient identifiers are blocked before posting."
            />
            <FeatureCard
              Icon={Smartphone}
              color="bg-rose-500"
              title="Mobile companion (beta)"
              description="iOS and Android companion app for digest reading, library access, community browsing, and audio playback on the go."
            />
            <FeatureCard
              Icon={Headphones}
              color="bg-indigo-500"
              title="Audio takeaways (Pro)"
              description="Text-to-speech summaries of each article. Build a commute playlist from your digest or library folder. Pro feature."
            />
          </div>
        </div>
      </section>

      {/* ── Tier comparison ─────────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6 bg-white" aria-labelledby="tiers-heading" data-testid="tier-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 id="tiers-heading" className="text-3xl font-bold text-slate-900 mb-3">
              Three ways to use LitCurrent
            </h2>
            <p className="text-slate-500 text-base max-w-lg mx-auto">
              Start free. Get validated to join the community. Go Pro to unlock time-saving tools.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map(tier => (
              <TierCard
                key={tier.id}
                tier={tier}
                trialsEnabled={trialsEnabled}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
              />
            ))}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Stripe billing coming soon. Pro access currently available via 30-day trial or on request.
          </p>
        </div>
      </section>

      {/* ── Trust / FAQ ─────────────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6 bg-slate-900" aria-labelledby="faq-heading">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 id="faq-heading" className="text-2xl font-bold text-white mb-2">Questions & trust</h2>
            <p className="text-slate-400 text-sm">What clinicians ask before signing up.</p>
          </div>
          <div>
            {FAQS.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ──────────────────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6 bg-sky-600 text-center" aria-labelledby="cta-heading">
        <div className="max-w-xl mx-auto">
          <h2 id="cta-heading" className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Ready to reclaim your reading time?
          </h2>
          <p className="text-sky-100 text-sm mb-8">
            Free plan · No credit card for sign-up · PHI-Zero compliant
          </p>
          <Button
            size="lg"
            className="bg-white text-sky-700 hover:bg-sky-50 px-8 text-base font-semibold rounded-xl shadow-md focus-visible:ring-2 focus-visible:ring-white"
            asChild
            data-testid="bottom-signup-btn"
          >
            <Link to="/signup" aria-label="Create your free LitCurrent account">Create your free account</Link>
          </Button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-slate-800 border-t border-slate-700 py-8 px-4 sm:px-6" role="contentinfo">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-slate-400 text-sm">© {new Date().getFullYear()} LitCurrent by Scienthesis</span>
          <nav className="flex flex-wrap justify-center gap-4 text-sm" aria-label="Footer navigation">
            <Link to="/login" className="text-slate-400 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-sky-400 rounded" data-testid="footer-login-link">Log in</Link>
            <Link to="/signup" className="text-slate-400 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-sky-400 rounded" data-testid="footer-signup-link">Sign up</Link>
            <Link to="/plan" className="text-slate-400 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-sky-400 rounded" data-testid="footer-plan-link">Plans</Link>
            <Link to="/phi-guidelines" className="text-slate-400 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-sky-400 rounded" data-testid="footer-phi-link">PHI Policy</Link>
          </nav>
        </div>
      </footer>

    </div>
  );
};

export default LandingPageNew;
